import { 
  Box, 
  Typography, 
  TextField, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  FormHelperText,
  Chip,
  Autocomplete,
  CircularProgress
} from '@mui/material';
import { useFormik } from 'formik';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Button } from '../../../../shared/components/ui/Button';
import { useAnnouncement } from '../../context/useAnnouncement';
import { announcementValidationSchema } from '../../validations/announcementValidationSchema';
import { ANNOUNCEMENT_TYPES, INITIAL_ANNOUNCEMENT_VALUES } from '../../constants/announcementConstants';
import type { Announcement, Unit, User } from '../../types/announcementTypes';
import { unitService } from '../../../../shared/services/unit/unitService';
import { userService } from '../../../../shared/services/users/usersServices';
import { useTenant } from '../../../../shared/context/TenantContext';
import { useAuth } from '../../../auth/context/AuthContext';
import { TenantSelectIfAdmin } from '../../../../shared/components/SelectsDinamycs/TenantSelectIfAdmin';
import { useState } from 'react';
import { sectorService } from '../../../../shared/services/sector/sectorService';


interface AnnouncementFormProps {
  announcement?: Announcement;
  isEdit: boolean;
}

export const AnnouncementForm: React.FC<AnnouncementFormProps> = ({ 
  announcement, 
  isEdit 
}) => {
  const navigate = useNavigate();
  const { createAnnouncement, updateAnnouncement, isCreating, isUpdating } = useAnnouncement();
  const { selectedTenant, handleSetSelectedTenant } = useTenant();
  const { user: authUser } = useAuth();
  const [selectedSector, setSelectedSector] = useState<string>('');

  const formik = useFormik({
    initialValues: announcement ? {
      title: announcement.title,
      description: announcement.description,
      type: announcement.type,
      models: announcement.models.map(m => ({ id: m.model_id }))
    } : INITIAL_ANNOUNCEMENT_VALUES,
    validationSchema: announcementValidationSchema,
    onSubmit: async (values) => {
      try {
        if (isEdit && announcement) {
          await updateAnnouncement(announcement.id, values);
        } else {
          if (!selectedTenant) {
            throw new Error('Nenhum tenant selecionado');
          }
          
          await createAnnouncement({
            ...values,
            tenant_id: selectedTenant.trim()
          });
        }
      } catch (error) {
        console.error('Erro ao salvar comunicado:', error);
      }
    }
  });


  const { data: sectorsResponse, isLoading: sectorsLoading } = useQuery({
    queryKey: ['sectors', selectedTenant],
    queryFn: () => sectorService.list(),
    enabled: formik.values.type === 'PER_APARTMENT' && !!selectedTenant
  });
  const sectors = sectorsResponse?.data || [];

  
  const { data: unitsResponse, isLoading: unitsLoading } = useQuery({
    queryKey: ['units', selectedSector],
    queryFn: () => unitService.list({ sectorId: selectedSector }),
    enabled: formik.values.type === 'PER_APARTMENT' && !!selectedSector
  });
  const units = unitsResponse?.data || [];


  const { data: usersResponse, isLoading: usersLoading } = useQuery({
    queryKey: ['users', 'condomino', selectedTenant],
    queryFn: () => userService.list({ tenantId: selectedTenant.trim() || undefined }),
    enabled: formik.values.type === 'PER_RESIDENT' && !!selectedTenant
  });
  const users = usersResponse?.data || [];

  const isLoading = isCreating || isUpdating;
  const isModelsLoading = unitsLoading || usersLoading;

  
  const getModelOptions = () => {
    switch (formik.values.type) {
      case 'PER_APARTMENT':
        return units.map((unit: Unit) => ({
          id: unit.id,
          label: `${unit.number}${unit.name ? ' - ' + unit.name : ''}`,
          value: unit.id
        }));
      case 'PER_RESIDENT':
        return users.map((user: User) => ({
          id: user.id,
          label: `${user.name} - ${user.email}`,
          value: user.id
        }));
      default:
        return [];
    }
  };

  // Valor selecionado para o autocomplete
  const getSelectedModels = () => {
    const options = getModelOptions();
    return formik.values.models
      .map((model: { id: string }) => options.find((option: { id: string }) => option.id === model.id))
      .filter(Boolean) as { id: string; label: string; value: string }[];
  };

  // Handler para mudança do tipo
  const handleTypeChange = (event: any) => {
    formik.handleChange(event);
    setSelectedSector('');
    // Limpar modelos selecionados quando mudar o tipo
    formik.setFieldValue('models', []);
  };

  // Handler para mudança dos modelos
  const handleModelsChange = (event: any, newValue: any[]) => {
    const models = newValue.map(option => ({ id: option.id }));
    formik.setFieldValue('models', models);
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
        <Button
          variant="outlined"
          onClick={() => navigate('/announcements')}
          sx={{ minWidth: 'auto', px: 2 }}
        >
          Voltar
        </Button>
        <Typography variant="h4" sx={{ fontWeight: 600, color: '#101E36' }}>
          {isEdit ? 'Editar Comunicado' : 'Novo Comunicado'}
        </Typography>
      </Box>

      {/* Form */}
      <form onSubmit={formik.handleSubmit}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Título */}
          <TextField
            fullWidth
            name="title"
            label="Título"
            value={formik.values.title}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.title && Boolean(formik.errors.title)}
            helperText={formik.touched.title && formik.errors.title}
            required
          />

          {/* Descrição */}
          <TextField
            fullWidth
            multiline
            rows={4}
            name="description"
            label="Descrição"
            value={formik.values.description}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.description && Boolean(formik.errors.description)}
            helperText={formik.touched.description && formik.errors.description}
            required
          />

          {/* Tipo */}
          <FormControl 
            fullWidth 
            error={formik.touched.type && Boolean(formik.errors.type)}
            required
          >
            <InputLabel>Tipo</InputLabel>
            <Select
              name="type"
              value={formik.values.type}
              onChange={e => {
                handleTypeChange(e);
                setSelectedSector('');
              }}
              onBlur={formik.handleBlur}
              label="Tipo"
            >
              {ANNOUNCEMENT_TYPES.map((type) => (
                <MenuItem key={type.value} value={type.value}>
                  {type.label}
                </MenuItem>
              ))}
            </Select>
            {formik.touched.type && formik.errors.type && (
              <FormHelperText>{formik.errors.type}</FormHelperText>
            )}
          </FormControl>

          {/* Se for apartamento, selecione o setor antes */}
          {formik.values.type === 'PER_APARTMENT' && (
            <FormControl
              fullWidth
              sx={{ mt: 2 }}
              disabled={sectorsLoading || !selectedTenant}
            >
              <InputLabel>Bloco/Setor</InputLabel>
              <Select
                value={selectedSector}
                label="Bloco/Setor"
                onChange={e => {
                  setSelectedSector(e.target.value);
                  formik.setFieldValue('models', []);
                }}
              >
                <MenuItem value="">Selecione</MenuItem>
                {sectors.map((sector: any) => (
                  <MenuItem key={sector.id} value={sector.id}>
                    {sector.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {/* Seleção de Modelos */}
          {formik.values.type !== 'ALL' && (
            <FormControl 
              fullWidth
              error={formik.touched.models && Boolean(formik.errors.models)}
              sx={{ mt: 2 }}
            >
              <Autocomplete
                multiple
                options={getModelOptions()}
                value={getSelectedModels()}
                onChange={handleModelsChange}
                loading={isModelsLoading}
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                disabled={
                  formik.values.type === 'PER_APARTMENT' && !selectedSector
                }
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      variant="outlined"
                      label={option.label}
                      {...getTagProps({ index })}
                      key={option.id}
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={
                      formik.values.type === 'PER_APARTMENT' 
                        ? 'Selecionar Apartamentos' 
                        : 'Selecionar Moradores'
                    }
                    placeholder={
                      formik.values.type === 'PER_APARTMENT'
                        ? 'Digite para buscar apartamentos...'
                        : 'Digite para buscar moradores...'
                    }
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {isModelsLoading ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                noOptionsText={
                  !selectedTenant
                    ? 'Selecione um tenant primeiro'
                    : formik.values.type === 'PER_APARTMENT' && !selectedSector
                      ? 'Selecione um bloco/setor primeiro'
                      : isModelsLoading 
                        ? 'Carregando...' 
                        : formik.values.type === 'PER_APARTMENT'
                          ? 'Nenhum apartamento encontrado'
                          : 'Nenhum morador encontrado'
                }
              />
              {formik.touched.models && formik.errors.models && (
                <FormHelperText error>
                  {Array.isArray(formik.errors.models) 
                    ? formik.errors.models.join(', ') 
                    : formik.errors.models}
                </FormHelperText>
              )}
            </FormControl>
          )}

          {isLoading && (
            <TenantSelectIfAdmin
              value={selectedTenant}
              onChange={(value: string) => {
                handleSetSelectedTenant(value);
                formik.setFieldValue('tenant_id', value);
              }}
              disabled={isLoading}
            />
          )}

          {/* Buttons */}
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 2 }}>
            <Button
              variant="outlined"
              onClick={() => navigate('/announcements')}
              disabled={isLoading}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={isLoading || !formik.isValid || (!selectedTenant && !isEdit)}
            >
              {isLoading ? 'Salvando...' : 'Salvar'}
            </Button>
          </Box>
        </Box>
      </form>
    </Box>
  );
};